import {useTranslation} from 'react-i18next';
import {ThreeDotsLoader, cn} from '@chekinapp/ui';
import {
  SPLASH_FADE_TRANSITION_CLASS,
  STAGE_LINE_KEYS,
  type SplashStage,
} from './constants';
import {SplashSlowActions} from './SplashSlowActions';

type Props = {
  stage: SplashStage;
  elapsedMs: number;
  onRetry: () => void;
  isHandingOff?: boolean;
  className?: string;
};

function SplashStatus({stage, elapsedMs, onRetry, isHandingOff, className}: Props) {
  const {t} = useTranslation();
  const stageLineKey = STAGE_LINE_KEYS[stage];
  const isSlow = stage === 'slow';
  const handOffClass = cn(
    SPLASH_FADE_TRANSITION_CLASS,
    isHandingOff && 'translate-y-[6px] opacity-0',
  );

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div
        className={cn(
          'mt-[22px] flex h-2.5 items-center justify-center kiosko:mt-[34px] kiosko:scale-[1.56]',
          isHandingOff && 'translate-y-[6px] opacity-0',
        )}
      >
        <ThreeDotsLoader
          width={41}
          height={41}
          color="var(--splash-animation-color)"
          className="motion-reduce:[&_span]:animate-none"
        />
      </div>

      <div
        className={cn(
          'mt-5 flex max-w-[300px] flex-col items-center gap-[9px] text-center desk:max-w-[380px] kiosko:mt-10 kiosko:max-w-[620px]',
          handOffClass,
        )}
      >
        <p
          aria-hidden="true"
          className={cn(
            'min-h-5 text-[16px] font-medium leading-5 text-[color:var(--sp-ink-soft)] kiosko:min-h-[30px] kiosko:text-[20px]',
            'transition-[opacity,transform] [transition-duration:320ms] motion-reduce:transition-none',
            stageLineKey ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
          )}
        >
          {stageLineKey ? t(stageLineKey) : null}
        </p>

        {isSlow && (
          <span className="rounded-full border border-[var(--sp-hairline)] px-2.5 py-[3px] text-[12px] font-bold tracking-[0.06em] text-[color:var(--sp-ink-faint)] tabular-nums">
            {t('splash_elapsed_seconds', {seconds: (elapsedMs / 1000).toFixed(1)})}
          </span>
        )}
      </div>

      {isSlow && <SplashSlowActions onRetry={onRetry} className={handOffClass} />}
    </div>
  );
}

export {SplashStatus};
